/**
 * UserSearch UI Component - Search and add friends
 */

import { friendsService } from '../../social';
import type { UserSearchResult } from '../../social/types';

export interface UserSearchOptions {
  onClose: () => void;
  onFriendAdded?: () => void;
}

export class UserSearch {
  private container: HTMLElement;
  private options: UserSearchOptions;
  private searchResults: UserSearchResult[] = [];
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private isLoading = false;

  constructor(container: HTMLElement, options: UserSearchOptions) {
    this.container = container;
    this.options = options;
    this.render();
  }

  /**
   * Handle search input
   */
  private async handleSearch(query: string): Promise<void> {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (query.length < 2) {
      this.searchResults = [];
      this.renderResults();
      return;
    }

    this.searchTimeout = setTimeout(async () => {
      this.isLoading = true;
      this.renderResults();

      const { data, error } = await friendsService.searchUsers(query);

      this.isLoading = false;

      if (data && !error) {
        this.searchResults = data;
      } else {
        this.searchResults = [];
      }

      this.renderResults();
    }, 300);
  }

  /**
   * Handle send friend request
   */
  private async handleSendRequest(userId: string): Promise<void> {
    const { error } = await friendsService.sendFriendRequest(userId);

    if (!error) {
      // Update the result to show pending
      this.searchResults = this.searchResults.map((result) => {
        if (result.id === userId) {
          return { ...result, friendshipStatus: 'pending' as const };
        }
        return result;
      });
      this.renderResults();

      if (this.options.onFriendAdded) {
        this.options.onFriendAdded();
      }
    }
  }

  /**
   * Handle accept friend request
   */
  private async handleAcceptRequest(friendshipId: string, userId: string): Promise<void> {
    const { error } = await friendsService.acceptFriendRequest(friendshipId);

    if (!error) {
      this.searchResults = this.searchResults.map((result) => {
        if (result.id === userId) {
          return { ...result, friendshipStatus: 'accepted' as const };
        }
        return result;
      });
      this.renderResults();

      if (this.options.onFriendAdded) {
        this.options.onFriendAdded();
      }
    }
  }

  /**
   * Render avatar
   */
  private renderAvatar(user: UserSearchResult): string {
    if (user.avatarUrl) {
      return `<img src="${user.avatarUrl}" alt="${user.username}" class="search-result-avatar" />`;
    }
    const initial = (user.displayName || user.username).charAt(0).toUpperCase();
    return `<div class="search-result-avatar-placeholder">${initial}</div>`;
  }

  /**
   * Get action button based on friendship status
   */
  private getActionButton(user: UserSearchResult): string {
    switch (user.friendshipStatus) {
      case 'accepted':
        return `<span class="search-result-status friends">Friends</span>`;
      case 'pending':
        return `<span class="search-result-status pending">Request Sent</span>`;
      case 'incoming':
        return `<button class="search-result-btn accept" data-id="${user.id}" data-friendship-id="${user.friendshipId}">Accept Request</button>`;
      case 'blocked':
        return `<span class="search-result-status blocked">Blocked</span>`;
      default:
        return `<button class="search-result-btn add" data-id="${user.id}">Add Friend</button>`;
    }
  }

  /**
   * Render search results
   */
  private renderResults(): void {
    const resultsContainer = this.container.querySelector('.search-results');
    if (!resultsContainer) return;

    if (this.isLoading) {
      resultsContainer.innerHTML = `
        <div class="search-loading">
          <div class="search-spinner"></div>
          <span>Searching...</span>
        </div>
      `;
      return;
    }

    if (this.searchResults.length === 0) {
      const input = this.container.querySelector('.search-input') as HTMLInputElement;
      const query = input?.value || '';
      
      if (query.length >= 2) {
        resultsContainer.innerHTML = `
          <div class="search-empty">
            <p>No users found matching "${query}"</p>
          </div>
        `;
      } else {
        resultsContainer.innerHTML = `
          <div class="search-empty">
            <p>Enter at least 2 characters to search</p>
          </div>
        `;
      }
      return;
    }

    resultsContainer.innerHTML = this.searchResults.map((user) => `
      <div class="search-result-card" data-id="${user.id}">
        <div class="search-result-info">
          ${this.renderAvatar(user)}
          <div class="search-result-details">
            <span class="search-result-name">${user.displayName || user.username}</span>
            <span class="search-result-username">@${user.username}</span>
          </div>
        </div>
        <div class="search-result-action">
          ${this.getActionButton(user)}
        </div>
      </div>
    `).join('');

    // Attach event listeners to buttons
    resultsContainer.querySelectorAll('.search-result-btn.add').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const userId = (e.target as HTMLElement).getAttribute('data-id');
        if (userId) {
          this.handleSendRequest(userId);
        }
      });
    });

    resultsContainer.querySelectorAll('.search-result-btn.accept').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const userId = target.getAttribute('data-id');
        const friendshipId = target.getAttribute('data-friendship-id');
        if (userId && friendshipId) {
          this.handleAcceptRequest(friendshipId, userId);
        }
      });
    });
  }

  /**
   * Render the search modal
   */
  render(): void {
    this.container.innerHTML = `
      <div class="search-modal-overlay">
        <div class="search-modal">
          <button class="search-modal-close">&times;</button>
          <h2 class="search-modal-title">Add Friend</h2>
          <p class="search-modal-subtitle">Search for users by username</p>
          
          <div class="search-input-wrapper">
            <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input 
              type="text" 
              class="search-input" 
              placeholder="Search by username..." 
              autofocus
            />
          </div>

          <div class="search-results">
            <div class="search-empty">
              <p>Enter at least 2 characters to search</p>
            </div>
          </div>
        </div>
      </div>
    `;

    // Attach event listeners
    this.container.querySelector('.search-modal-close')?.addEventListener('click', () => {
      this.options.onClose();
    });

    this.container.querySelector('.search-modal-overlay')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.options.onClose();
      }
    });

    const input = this.container.querySelector('.search-input') as HTMLInputElement;
    input?.addEventListener('input', (e) => {
      this.handleSearch((e.target as HTMLInputElement).value);
    });

    // Handle escape key
    document.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Handle keyboard events
   */
  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.options.onClose();
    }
  };

  /**
   * Cleanup
   */
  destroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.container.remove();
  }
}
